import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LogError, Metadata, RunView } from '@memberjunction/core';
import { arrowLeftIcon } from '@progress/kendo-svg-icons';
import { InstanceContactEntity, InstanceContactEntityExtended, InstanceContactRoleEntity, InstanceEntityExtended, InstanceRoleEntity, OrganizationContactEntity, OrganizationContactEntityExtended, SubscriptionEntityExtended } from 'mj_generatedentities';
import { UserContextService } from 'src/app/core/services/user-context.service';
import { SharedService } from 'src/app/core/services/shared-service';
import { SubscriptionPreviewOptions } from 'src/app/core/services/subscription-plan.service';

export class CombinedContact {
  public oc: OrganizationContactEntityExtended;
  public ic: InstanceContactEntityExtended | undefined;
  public savedRoles: InstanceContactRoleEntity[] = [];
  public selectedRoles: InstanceRoleEntity[] = [];
  
  constructor() {}
 
  public get RolesDirty(): boolean {
    // compare the role Ids in the selectedRoles array with the savedRoles array
    if (this.savedRoles.length !== this.selectedRoles.length) {
      return true;
    }
    // check to see if any of the selected roles are not in the saved roles
    for (const role of this.selectedRoles) {
      if (!this.savedRoles.find(sr => sr.RoleID === role.ID)) {
        return true;
      }
    }
    // check to see if any of the saved roles are not in the selected roles
    for (const role of this.savedRoles) {
      if (!this.selectedRoles.find(sr => sr.ID === role.RoleID)) {
        return true;
      }
    }
    return false;
  }

  public get Dirty(): boolean {
    return this.ic?.Dirty || this.RolesDirty;
  }

}

@Component({
  selector: 'app-instance-detail',
  templateUrl: './instance-detail.component.html',
  styleUrl: './instance-detail.component.css'
})
export class InstanceDetailComponent implements OnInit{
  _md: Metadata;
  public instanceID: string = '';
  public Instance: InstanceEntityExtended | undefined;
  public Subscription: SubscriptionEntityExtended;
  public InstanceContacts: InstanceContactEntityExtended[] = [];
  public organizationContacts: OrganizationContactEntityExtended[] = [];
  public ActiveInstanceContactList: CombinedContact[] = [];
  public InactiveInstanceContactList: CombinedContact[] = [];
  public InstanceRoles: InstanceRoleEntity[] = [];
  public InstanceContactRoles: InstanceContactRoleEntity[] = [];

  public IsLoading: boolean = true;
  public IsSaving: boolean = false;
  public LoadingMessage: string = 'Loading instance data';
  public possibleStatusValues: string[] = [];
  public possibleAccessLevels: string[] = [];

  // "Remove user" confirmation dialog
  public showRemoveUserDialog = false;
  public isPreviewLoaded = false;
  public userToRemove: InstanceContactEntityExtended | null = null;
  public previewOptions: SubscriptionPreviewOptions = null;

  // "Add user" dialog
  public showAddUserPopup: boolean = false;
  public newUserDialogPage: number = 1;
  // newSelectedContacts holds the IDs of organization contacts (from InactiveInstanceContactList)
  public newSelectedContacts: string[] = [];
  public addPreviewLoaded: boolean = false;
  public availableSlots: number = 0;

  public arrowLeftIcon = arrowLeftIcon;

  constructor(
    private userContextService: UserContextService,
    private sharedService: SharedService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this._md = new Metadata();
    const eInstances = this._md.EntityByName("Instances");
    this.possibleStatusValues = eInstances?.Fields.find(f => f.Name === "Status")?.EntityFieldValues.map(efv => efv.Code) || [];
    this.possibleAccessLevels = eInstances?.Fields.find(f => f.Name === "AccessLevel")?.EntityFieldValues.map(efv => efv.Code) || [];
  }

  async ngOnInit(): Promise<void> {
    this.instanceID = this.route.snapshot.paramMap.get('instanceId');
    this.loadData();
  }

  public async loadData() {
    this.IsLoading = true;

    await this.loadOrganizationContacts();
    await this.loadInstanceData();

    this.IsLoading = false;
  }

  public async loadInstance(): Promise<InstanceEntityExtended> {
    const instance = await this._md.GetEntityObject<InstanceEntityExtended>("Instances");

    if (!(await instance.Load(this.instanceID))) {
      this.sharedService.DisplayNotification("Error loading instance", "error");
      this.router.navigate(['/settings/instance-list']);
      return null;
    }

    return instance;
  }

  public async loadOrganizationContacts() {
    const systemAdminRole = this.userContextService.SystemAdminRole;
    const rv = new RunView();
    const result = await rv.RunView<OrganizationContactEntityExtended>({
      EntityName: "Organization Contacts", 
      ExtraFilter: `Active=1 AND 
                    ContactRoleID != '${systemAdminRole.ID}' AND
                    OrganizationID='${this.userContextService.Organization.ID}'`,
      OrderBy: 'Contact',
      ResultType: 'entity_object'
    })
    if (result.Success) {
      this.organizationContacts = result.Results;
    }
  }

  public async loadInstanceData() {
    const rv = new RunView();
    const pInstanceContacts = rv.RunView<InstanceContactEntityExtended>({
      EntityName: 'Instance Contacts',
      ExtraFilter: `InstanceID='${this.instanceID}'`,
      OrderBy: 'Contact',
      ResultType: 'entity_object'
    });

    const pRoles = rv.RunView<InstanceRoleEntity>({
      EntityName: 'Instance Roles',
      ExtraFilter: `InstanceID='${this.instanceID}'`,
      ResultType: 'entity_object'
    });

    const eInstanceContacts = this._md.EntityByName("Instance Contacts");
    const pInstanceContactRoles = rv.RunView<InstanceContactRoleEntity>({
      EntityName: 'Instance Contact Roles',
      ExtraFilter: `InstanceContactID IN(SELECT ID FROM ${eInstanceContacts.SchemaName}.${eInstanceContacts.BaseView} WHERE InstanceID='${this.instanceID}')`,
      ResultType: 'entity_object'
    });

    const pInstance = this.loadInstance();

    const [instanceContacts, roles, instanceContactRoles, instance] = await Promise.all([pInstanceContacts, pRoles, pInstanceContactRoles, pInstance]);

    if (instanceContacts.Success) {
      this.InstanceContacts = instanceContacts.Results;
    }
    if (roles.Success) {
      this.InstanceRoles = roles.Results;
    }
    if (instanceContactRoles.Success) {
      this.InstanceContactRoles = instanceContactRoles.Results;
    }
    if (instance) {
      this.Instance = instance;
      this.Subscription = this.userContextService.Subscriptions.find(s => s.ID === this.Instance.SubscriptionID);
      this.availableSlots = (await this.Subscription.GetPlanConstraints()).MaxUser - this.Subscription.CurrentUserCount;
    }

    // now build the instanceContactAccessList which is a mashup of the org contacts and the instance conntacts data
    // build the array here
    const instanceContactList = this.organizationContacts.map(oc => {
      const ic = this.InstanceContacts.find(ic => ic.ContactID === oc.ContactID);
      const newItem = new CombinedContact();
      newItem.oc = oc;
      newItem.ic = ic;
      if (ic) {
        newItem.savedRoles = this.InstanceContactRoles.filter(icr => icr.InstanceContactID === ic.ID)
        newItem.selectedRoles = this.InstanceRoles.filter(ir => newItem.savedRoles.find(sr => sr.RoleID === ir.ID));  
      }
      return newItem;
    })

    // split the list into active and inactive
    this.ActiveInstanceContactList = instanceContactList.filter(item => item.ic?.IsActive);
    this.InactiveInstanceContactList = instanceContactList.filter(item => !item.ic?.IsActive);
  }

  public showAllInstances(): void {
    this.router.navigate(['/settings/instance-list']);
  }

  public get instanceDirty(): boolean {
    if (!this.Instance) {
      return false;
    }
    else {
      return this.Instance.Dirty || 
             this.ActiveInstanceContactList.find(item => item.Dirty) !== undefined;
    }
  }


  public onInstanceRolesChanged(value: any) {
    console.log(value);
  }
 
  public async onIsActiveChanged(event: Event, dataItem: any) {
    const isChecked = (event.target as HTMLInputElement).checked;

    // Perform any additional logic, such as updating a model or calling a service
    const ic: InstanceContactEntity = dataItem.ic;
    if (ic) {
      ic.IsActive = isChecked;
      // check to see if the instance contact was ever saved. If not, the user had checked and unchecked the active box so we can just get rid of it
      if (!isChecked && !ic.IsSaved) {
        dataItem.ic = undefined;
      }
    }
    else {
      // no instance contact yet exists, create the object
      const ic = await this._md.GetEntityObject<InstanceContactEntity>("Instance Contacts");
      ic.NewRecord();
      ic.InstanceID = this.Instance.ID;
      ic.ContactID = dataItem.oc.ContactID;
      ic.IsActive = isChecked;
      dataItem.ic = ic;
    }
  }

  public async saveInstanceDetails() {
    // check to see if we're dirty just in case we get a call and nothing is to be done
    if (this.instanceDirty) {
      this.LoadingMessage = 'Saving instance data...';
      this.IsSaving = true;
      let ok: boolean = true;
      // we need to go through each item in the instanceContactList and save the instance contact
      for (const item of this.ActiveInstanceContactList) {
        if (item.ic?.Dirty) {
          ok = ok && await item.ic.Save(); // save right away, we need the ID for role stuff
        }
      }

      // next we need to save the instance contact roles so they match the selected roles
      for (const item of this.ActiveInstanceContactList) {
        if (item.ic) {
          // get the roles that were saved and are no longer selected
          const rolesToRemove = item.savedRoles.filter(sr => !item.selectedRoles.find(r => r.ID === sr.RoleID));
          for (const role of rolesToRemove) {
            item.savedRoles.splice(item.savedRoles.indexOf(role), 1); // remove from the saved roles list
            ok = ok && await role.Delete(); // for deletes we do await, but with TG they come bakc right away, we'll submit the tg later
          }

          // get the roles that are selected and not saved
          const rolesToAdd = item.selectedRoles.filter(r => !item.savedRoles.find(sr => sr.RoleID === r.ID));
          for (const role of rolesToAdd) {
            const icr = await this._md.GetEntityObject<InstanceContactRoleEntity>("Instance Contact Roles");
            item.savedRoles.push(icr); // add to the saved roles list
            icr.NewRecord();
            icr.InstanceContactID = item.ic.ID;
            icr.RoleID = role.ID;
            ok = ok && await icr.Save();  
          }
        }
      }

      if (ok && await this.Instance.Save()) {
        // success, show notification
        // update the data structure 
        this.IsSaving = false;
        this.sharedService.DisplayNotification("Instance Saved", "success");
      }
      else {
        // failed, show notification
        this.sharedService.DisplayNotification("Error saving instance contact details", "error");
        this.IsSaving = false;
      }
    }
  }

  public isEditingInstanceRole: boolean = false;
  public selectedInstanceRole: InstanceRoleEntity | undefined;
  public async saveSelectedInstanceRole() {
    if (this.selectedInstanceRole) {
      if (!await this.selectedInstanceRole.Save()) {
        this.sharedService.DisplayNotification("Error saving instance role", "error");
      }
      else {
        await this.loadInstanceData();
        this.isEditingInstanceRole = false;
      }
    }
  }
  
  public cancelEditingInstanceRole() {
    this.isEditingInstanceRole = false;
  }

  public async editInstanceRole(item: InstanceRoleEntity) {
    this.selectedInstanceRole = item;
    this.isEditingInstanceRole = true;
  }

  public async addNewInstanceRole() {
    const ir = await this._md.GetEntityObject<InstanceRoleEntity>("Instance Roles");
    ir.NewRecord();
    ir.InstanceID = this.Instance.ID;
    this.selectedInstanceRole = ir;
    this.isEditingInstanceRole = true;
  }

  public async deleteInstanceRole(item: InstanceRoleEntity) {
    if (item) {
      if (!await item.Delete()) {
        this.sharedService.DisplayNotification("Error deleting instance role", "error");
      }
      else {
        await this.loadInstanceData();
      }
    }
  }

  onPreviewLoaded(isLoaded: boolean): void {
    this.isPreviewLoaded = isLoaded;
  }

  onRemoveUser(user: CombinedContact): void {
    const instanceContact = user.ic;
    this.userToRemove = instanceContact;
    this.previewOptions = { NewUserCount: this.Subscription.CurrentUserCount - 1 };
    this.showRemoveUserDialog = true;
  }

  async confirmRemoveUser(): Promise<void> {
    if (!this.userToRemove) return;

    this.LoadingMessage = 'Removing user...';
    this.IsLoading = true;
    this.IsSaving = true;

    try {
      this.userToRemove.IsActive = false;
      if (await this.userToRemove.Save()) {
        // remove the instance contact from the active list and move it to the inactive list
        const index = this.ActiveInstanceContactList.findIndex(item => item.ic?.ID === this.userToRemove.ID);
        if (index >= 0) {
          const item = this.ActiveInstanceContactList[index];
          this.ActiveInstanceContactList.splice(index, 1);
          this.InactiveInstanceContactList.push(item);
        }

        this.sharedService.DisplayNotification('User removed successfully', 'success');
      } else {
        const errorMessage = this.userToRemove.LatestResult.Errors?.map(e => e.Message).join('\n') || this.userToRemove.LatestResult.Message;
        LogError(`Failed to remove user:\n${errorMessage}`);
        this.sharedService.DisplayNotification('Failed to remove user', 'error');
      }
    } finally {
      this.IsLoading = false;
      this.IsSaving = false;
      this.isPreviewLoaded = false;
      this.showRemoveUserDialog = false;
      this.userToRemove = null;
      this.previewOptions = null;
    }
  }

  public openAddUserPopup(): void {
    this.newUserDialogPage = 1;
    this.newSelectedContacts = [];
    this.addPreviewLoaded = false;
    this.showAddUserPopup = true;
  }

  public closeAddUserPopup(): void {
    this.showAddUserPopup = false;
  }

  public toggleContactSelection(contactId: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) {
      if (this.newSelectedContacts.length < this.availableSlots) {
        this.newSelectedContacts.push(contactId);
      } else {
        (event.target as HTMLInputElement).checked = false;
      }
    } else {
      this.newSelectedContacts = this.newSelectedContacts.filter(id => id !== contactId);
    }
  }

  public isContactSelected(contactId: string): boolean {
    return this.newSelectedContacts.includes(contactId);
  }

  public getPreviewOptionsForAdd(): SubscriptionPreviewOptions {
    return {
      NewUserCount: this.Subscription.CurrentUserCount + this.newSelectedContacts.length
    };
  }

  public onAddPreviewLoaded(isLoaded: boolean): void {
    this.addPreviewLoaded = isLoaded;
  }

  public async confirmAddUsers(): Promise<void> {
    this.LoadingMessage = 'Adding users...';
    this.IsSaving = true;
    try {
      // Loop through each selected inactive contact and activate them (or create a new instance contact if none exists)
      for (const contactId of this.newSelectedContacts) {
        const combined = this.InactiveInstanceContactList.find(item => item.oc.ContactID === contactId);
        if (combined) {
          if (combined.ic) {
            combined.ic.IsActive = true;
            await combined.ic.Save();
          } else {
            const newIc = await this._md.GetEntityObject<InstanceContactEntityExtended>('Instance Contacts');
            newIc.NewRecord();
            newIc.InstanceID = this.Instance.ID;
            newIc.ContactID = combined.oc.ContactID;
            newIc.IsActive = true;
            await newIc.Save();
            combined.ic = newIc;
          }
        }
      }
      // Optionally update the subscription user count if needed here.
      await this.loadInstanceData();
      this.sharedService.DisplayNotification('Users added successfully', 'success');
    } catch (error) {
      this.sharedService.DisplayNotification('Error adding users', 'error');
      LogError('Error adding users to instance:', error);
    } finally {
      this.IsSaving = false;
      this.closeAddUserPopup();
    }
  }
}
